<template>
  <div class="skill-impact-chart">
    <canvas ref="chartCanvas"></canvas>

    <div v-if="loading" class="chart-overlay">
      <span class="dx-loader" aria-hidden="true"></span>
    </div>

    <div v-else-if="impactData.length === 0" class="chart-overlay">
      <p class="gm-empty__hint">This skill declares no impact to plot.</p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import type { ImpactChartData } from '@/types/skillVisualization'
import useImpactChart from '@/composables/useImpactChart'

interface Props {
  impactData: ImpactChartData[]
  xAxisLabel?: string
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  xAxisLabel: 'Stat Value',
  loading: false
})

const chartCanvas = ref<HTMLCanvasElement | null>(null)
const { updateChart, destroyChart } = useImpactChart(chartCanvas)

const render = () => {
  if (!chartCanvas.value) return
  if (props.impactData.length === 0) {
    destroyChart()
    return
  }
  updateChart(props.impactData, props.xAxisLabel)
}

watch(() => [props.impactData, props.xAxisLabel], render, { deep: true })

onMounted(render)
</script>

<style scoped>
/* The canvas is sized by Chart.js from this box, so the box must not be sized
   by the canvas — hence the absolute fill rather than `height: 100%`, which
   would feed the canvas's own height back into the layout and grow every
   redraw. */
.skill-impact-chart {
  position: relative;
  flex: 1;
  /* The chart is the point of the screen, so it holds a floor rather than
     yielding the last of a short window to the header and the impact strip. */
  min-height: 12rem;
  padding: var(--gm-gap-sm) var(--gm-gap);
}

.skill-impact-chart canvas {
  position: absolute;
  inset: var(--gm-gap-sm) var(--gm-gap);
}

.chart-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--gm-gap-sm);
  background: var(--gm-surface-1);
}
</style>
